import React, { useRef, useState } from "react";
import { useNavigate } from "react-router";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import type { TrashModel } from "target_wasm";
import { ModelElementType } from "@/components/entities";
import { AssetIcon } from "@/components/common/AssetIcon";
import { Spinner } from "@/components/common/Spinner";
import { AppearancePicker } from "@/components/common/AppearancePicker";
import { Button } from "@/components/common/Button";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "@/components/common/TooltipButton";
import { StatusChip } from "@/components/common/StatusChip";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { EditableName } from "@/components/canvas/utils/EditableName";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import { checkCanConnect } from "@/components/canvas/utils/CanvasActions";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useModelActions } from "@/components/main/utils/models/useModelActions";
import { trashRiskColor } from "@/components/main/utils/models/types";
import { DeleteModelDialog } from "@/components/main/utils/models/deletion/DeleteModelDialog";
import { useModelAppearanceUpdate } from "@/components/main/utils/models/useModelAppearance";
import { canEditModelMeta } from "@/components/main/utils/models/types";
import { ownershipLabel } from "./selectors";
import { LiveIndicator } from "./LiveIndicator";

interface ModelRowProps {
  model: ModelElementType;
  /** Right-aligned mono metadata (e.g. compact relative time). */
  meta?: string;
  /** Show the "N active users" label beside the live dot (All-models list). */
  showActiveCount?: boolean;
  /** Freshly created — plays the settle-in outline animation. */
  isFresh?: boolean;
  onDuplicateSession?: (message: string) => void;
  /** Team-context rendering: swaps Delete → "Dissociate from team" and shows the
   *  team's access label. Defaults keep the plain overview behavior. */
  isTeamContext?: boolean;
  teamId?: string;
  canDissociate?: boolean;
  teamAccess?: string;
  /** Where canvas returns on close (defaults to the overview). */
  returnPath?: string;
  /** Overrides the ownership label (the muted "Owner"/"Shared" word) — the
   *  Shared-with-me page puts the caller's relation or "via space" there.
   *  Catalog and trash renderings still win over it. */
  sub?: React.ReactNode;
  /** Trash rendering: the row can't be opened, the role label is hidden, the
   *  meta shows the deletion countdown, and the trailing star is replaced by
   *  hover-revealed Restore / Delete-forever actions. `trashModel` also drives
   *  the scheduled-tables pill, which reopens the deletion analysis so those
   *  tables can be transferred instead of lost. */
  trash?: {
    countdownLabel: string;
    countdownTooltip: string;
    onRestore: () => void;
    onDeleteForever: () => void;
    trashModel: TrashModel;
    onAnalysisClosed?: () => void;
    /// D53: set while the model's space is in its deletion window — the model
    /// comes back with the space, so its own Restore is shown disabled with
    /// this as the reason.
    restoreLocked?: string;
  };
}

/** Item-height quiet row on a fixed grid so meta and star columns align across
 *  the page: icon · name+role · meta · star. Row click opens the model;
 *  the icon opens the appearance picker, the hover pencil renames; a
 *  right-click opens the model actions menu. */
export const ModelRow: React.FC<ModelRowProps> = ({
  model,
  meta,
  showActiveCount = false,
  isFresh = false,
  onDuplicateSession,
  isTeamContext = false,
  teamId,
  canDissociate = false,
  teamAccess,
  returnPath = "/overview",
  sub,
  trash,
}) => {
  const navigate = useNavigate();
  const iconRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const menu = useContextMenu();
  const toggleStar = useMainStateStore((state) => state.toggleStar);
  const isStarred = model.is_starred ?? false;
  const canEdit = canEditModelMeta(model) && !model.scheduled_deletion_date;
  // A model scheduled for deletion can't be entered — it's on its way to Trash.
  const canOpen = !trash && !model.scheduled_deletion_date;

  const actions = useModelActions(model, {
    isTeamContext,
    teamId,
    canDissociate,
  });

  const handleAppearanceChange = useModelAppearanceUpdate(model);

  const commitName = actions.commitName;

  const openModel = async (): Promise<void> => {
    if (!canOpen) return;
    if (isEditing || isPickerOpen) return;
    if (!navigator.onLine) return;
    const clientId = String(localStorage.getItem("clientID"));
    const canConnect = await checkCanConnect(clientId, String(model.model_id));
    if (!canConnect) {
      onDuplicateSession?.(
        "This model is already active in a different tab or window!",
      );
      return;
    }
    sessionStorage.setItem("teamReturnPath", returnPath);
    navigate(`/canvas/${String(model.model_id)}`);
  };

  const scheduledTableCount =
    trash?.trashModel.tables_scheduled_for_deletion_count ?? 0;

  const contextMenuItems: ContextMenuItem[] = trash
    ? [
        {
          label: trash.restoreLocked ? "Restore (with its space)" : "Restore",
          icon: <RestoreIcon className="icon-md" />,
          onClick: trash.onRestore,
          disabled: !!trash.restoreLocked,
        },
        {
          label: "Review scheduled tables",
          icon: <TableChartOutlinedIcon className="icon-md" />,
          onClick: () => setIsAnalysisOpen(true),
          disabled: scheduledTableCount === 0,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Delete forever",
          icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
          onClick: trash.onDeleteForever,
          danger: true,
        },
      ]
    : [
        {
          label: "Open",
          icon: <OpenInFullIcon className="icon-md" />,
          onClick: () => void openModel(),
          disabled: !canOpen,
        },
        {
          // A new tab gets its own session, so it runs its own single-session
          // check on load — no need to pre-flight `checkCanConnect` here.
          label: "Open in a new tab",
          icon: <OpenInNewIcon className="icon-md" />,
          onClick: () =>
            window.open(
              `/canvas/${String(model.model_id)}`,
              "_blank",
              "noopener,noreferrer",
            ),
          disabled: !canOpen,
        },
        { label: "", onClick: () => {}, divider: true },
        {
          label: "Rename",
          icon: <EditOutlinedIcon className="icon-md" />,
          onClick: () => setIsEditing(true),
          disabled: !canEdit,
        },
        {
          label: "Change appearance",
          icon: <PaletteOutlinedIcon className="icon-md" />,
          onClick: () => setIsPickerOpen(true),
          disabled: !canEdit,
        },
        {
          label: "Manage access",
          icon: <ShareOutlinedIcon className="icon-md" />,
          onClick: actions.openShare,
          disabled: !actions.permissions.canSeeSharedList,
        },
        {
          label: "Copy model ID",
          icon: <ContentCopyIcon className="icon-md" />,
          onClick: actions.copyModelId,
        },
        { label: "", onClick: () => {}, divider: true },
        isTeamContext
          ? {
              label: "Remove from team",
              icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
              onClick: actions.dissociate,
              disabled: !actions.permissions.canDissociateModel,
              danger: true,
            }
          : {
              label: "Delete",
              icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
              onClick: actions.requestDelete,
              disabled: !actions.permissions.canDeleteModel,
              danger: true,
            },
      ];

  const activeCount = model.active_sessions?.length ?? 0;

  // Both popovers are portaled, which takes the pointer off the row and drops
  // `:hover` — pin the row's hover state while either is open.
  const isRowPinned = menu.isOpen || isPickerOpen;

  return (
    <>
      <div
        className={`group flex items-center gap-2 item-height px-1 rounded-app hover:bg-bg-hover ${
          isRowPinned ? "bg-bg-hover" : ""
        } ${canOpen ? "cursor-pointer" : "cursor-default"} ${
          isFresh ? "overview-fresh" : ""
        }`}
        tabIndex={0}
        onClick={canOpen ? () => void openModel() : undefined}
        {...menu.triggerProps}
      >
        <div className="relative shrink-0" ref={iconRef}>
          <span
            role={canEdit ? "button" : undefined}
            aria-label={canEdit ? "Change model appearance" : undefined}
            className={`flex items-center justify-center rounded-app p-0.5 ${
              canEdit ? "hover:bg-bg-hover" : ""
            }`}
            onClick={(e) => {
              if (!canEdit || actions.isDeleting) return;
              e.stopPropagation();
              // Open-only: a repeated click while open is ignored (the
              // picker also ignores anchor mousedowns) — no close-reopen flash.
              setIsPickerOpen(true);
            }}
          >
            {actions.isDeleting ? (
              <Spinner size="sm" />
            ) : (
              <AssetIcon size="sm" color={model.color} iconIndex={model.icon} />
            )}
          </span>
          <AppearancePicker
            currentColor={model.color}
            currentIcon={model.icon}
            onColorChange={(color) => void handleAppearanceChange({ color })}
            onIconChange={(icon) => void handleAppearanceChange({ icon })}
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            disabled={!canEdit}
            anchorRef={iconRef}
          />
        </div>
        {/* `min-w-16` is the floor that keeps the model identifiable while the
            space label expands over it on hover — without it the name
            collapses to a single letter. */}
        <span className="text-fg-medium flex items-center min-w-16 shrink">
          <EditableName
            name={model.name || "Unnamed Model"}
            isEditing={isEditing}
            canEdit={canEdit}
            editTrigger="icon"
            editColor={model.color || undefined}
            setIsEditing={setIsEditing}
            update={() => {}}
            commit={commitName}
            abort={() => setIsEditing(false)}
          />
        </span>
        {!trash &&
          (model.is_catalog ? (
            <StatusChip tone="warning">Catalog</StatusChip>
          ) : (
            <span className="text-fg-muted-regular-small shrink-0">
              {sub ??
                (isTeamContext
                  ? (teamAccess ?? ownershipLabel(model.relation))
                  : ownershipLabel(model.relation))}
            </span>
          ))}
        <span className="flex-1" />
        {trash ? (
          <>
            {/* The tint carries the worst risk among the scheduled tables;
                clicking opens the analysis, where they can be transferred to
                another model instead of going with the timer. */}
            <span className="flex items-center gap-2 shrink-0">
              {scheduledTableCount > 0 && (
                <Tooltip
                  title={`${scheduledTableCount} ${scheduledTableCount === 1 ? "table" : "tables"} scheduled for deletion — review or transfer`}
                  placement="top"
                >
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Review ${scheduledTableCount} scheduled ${scheduledTableCount === 1 ? "table" : "tables"} in ${model.name || "Unnamed Model"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnalysisOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAnalysisOpen(true);
                    }}
                    className={`inline-flex items-center gap-1 shrink-0 px-1 py-px rounded-app font-mono select-none cursor-pointer hover:bg-bg-hover transition-colors ${trashRiskColor(trash.trashModel)}`}
                    style={{
                      fontSize: "var(--font-size-x-small)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <TableChartOutlinedIcon className="icon-xs" />
                    {scheduledTableCount}
                  </span>
                </Tooltip>
              )}
              {/* No fixed width — the Restore column to its right is fixed, so
                  the label's right edge still lines up across rows. */}
              <Tooltip title={trash.countdownTooltip} placement="top">
                <span
                  className="font-mono text-fg-muted shrink-0 cursor-default"
                  style={{
                    fontSize: "var(--font-size-x-small)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {trash.countdownLabel}
                </span>
              </Tooltip>
            </span>
            <span
              className={`flex items-center gap-1 shrink-0 transition-opacity ${
                isRowPinned
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
              }`}
            >
              <TooltipButton
                title={trash.restoreLocked ?? "Restore"}
                variant="contextual"
                size="xs"
                aria-label="Restore model"
                disabled={!!trash.restoreLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  trash.onRestore();
                }}
              >
                <RestoreIcon />
              </TooltipButton>
            </span>
          </>
        ) : (
          // Restore is the one-click path out of Trash; "Delete forever" is
          // irreversible, so it lives in the context menu only.
          <>
            <LiveIndicator count={activeCount} showCount={showActiveCount} />
            {!isTeamContext && <ModelSpaceLabel model={model} />}
            <span
              className="font-mono text-fg-muted text-right shrink-0 w-12"
              style={{
                fontSize: "var(--font-size-x-small)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {meta ?? ""}
            </span>
            <span
              className={`flex justify-end shrink-0 w-6 transition-opacity ${
                isStarred || isRowPinned
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <Button
                variant="default"
                size="xs"
                aria-label={isStarred ? "Unpin model" : "Pin model"}
                onClick={(e) => {
                  e.stopPropagation();
                  void toggleStar(model.model_id, !isStarred);
                }}
              >
                {isStarred ? <StarRoundedIcon /> : <StarBorderOutlinedIcon />}
              </Button>
            </span>
          </>
        )}
      </div>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${model.name || "Unnamed Model"}`}
      />

      {/* Trash mode drops the destructive action and shows a transfer-only
          layout, so this rescues scheduled tables rather than deleting. Mounted
          only while open — one idle dialog per trash row is pure overhead, and
          `Dialog` has no exit animation to preserve. */}
      {trash && isAnalysisOpen && (
        <DeleteModelDialog
          open
          onClose={() => {
            setIsAnalysisOpen(false);
            trash.onAnalysisClosed?.();
          }}
          model={model}
          onDelete={async () => setIsAnalysisOpen(false)}
          isFromTrash
          trashModel={trash.trashModel}
        />
      )}

      {actions.dialogs}
    </>
  );
};

/// The model's home space (S6a stamp), as a quiet dot + name. Renders only
/// when the space is in the active org's visible tree — a foreign or
/// unstamped model just shows nothing extra.
/// The model's home space, as a colour dot that expands into its name on
/// hover. Collapsed by default because the name is the least important thing
/// in the row and a long one ("Maximilian's Space") crowded the model name
/// down to an ellipsis. `max-width` carries the transition — `width: auto` is
/// not animatable — and the name itself truncates so a very long space can
/// never reclaim the row.
const ModelSpaceLabel: React.FC<{ model: ModelElementType }> = ({ model }) => {
  const space = useOrgContextStore((s) => s.spaceById(model.space_id));
  if (!space) return null;
  const color = resolveDatasetColor(spaceColor(space));
  return (
    <span
      className="group/space flex shrink-0 items-center font-mono text-fg-muted"
      style={{ fontSize: "var(--font-size-x-small)" }}
      title={space.name}
    >
      <span
        aria-hidden
        className="h-1.5 w-1.5 shrink-0 rounded-app"
        style={{ backgroundColor: color }}
      />
      <span className="sr-only">{space.name}</span>
      {/* The leading gap lives inside the clipped box, so the collapsed state
          is exactly the dot — no dead space in the row. */}
      <span
        aria-hidden
        className="max-w-0 overflow-hidden pl-1.5 text-ellipsis whitespace-nowrap opacity-0 group-hover/space:max-w-32 group-hover/space:opacity-75"
        style={{
          transitionProperty: "max-width, opacity",
          transitionDuration: "var(--duration-short)",
          transitionTimingFunction: "var(--ease-enter)",
        }}
      >
        {space.name}
      </span>
    </span>
  );
};
